import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Bell, BellOff, Check, CheckCheck, Trash2,
  ExternalLink, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PushActivationCard } from "@/components/PushActivationCard";

/**
 * Meddelandecentral — visar all push-historik för inloggad användare.
 *
 * Varje rad är expanderbar (visar full text + data-fält). Knappar för
 * markera-läst, radera, och navigera till push:ens deep-link (om sådan finns
 * via `data.url`). Filter: alla / olästa.
 */
export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", {
        params: { only_unread: onlyUnread, limit: 200 },
      });
      setItems(data?.items || []);
      setUnreadCount(data?.unread_count || 0);
    } catch {
      toast.error("Kunde inte hämta meddelanden");
    } finally {
      setLoading(false);
    }
  }, [onlyUnread]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems(items.map((it) => (it.id === id ? { ...it, read_at: new Date().toISOString() } : it)));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch { /* tyst */ }
  };

  const markAllRead = async () => {
    try {
      const { data } = await api.post("/notifications/read-all");
      toast.success(`${data.marked} meddelanden markerade som lästa`);
      load();
    } catch {
      toast.error("Kunde inte markera alla som lästa");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems(items.filter((it) => it.id !== id));
    } catch {
      toast.error("Kunde inte radera");
    }
  };

  const openDeepLink = async (it) => {
    if (!it.read_at) await markRead(it.id);
    const url = it.data?.url;
    if (url) {
      // Intern route → använd react-router
      if (url.startsWith("/")) navigate(url);
      else window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="notifications-page">
      <header className="px-4 pt-4 pb-3 flex items-center gap-2 sticky top-0 bg-[color:var(--bg-canvas)] z-10 border-b border-[color:var(--border-subtle)]">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-lg hover:bg-[color:var(--bg-elevated)]"
          aria-label="Tillbaka"
          data-testid="notifications-back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-wider text-[color:var(--text-tertiary)]">Meddelanden</p>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Bell size={20} />
            <span>Notiscentral</span>
            {unreadCount > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-[color:var(--jarvis)] text-white font-bold" data-testid="notifications-unread-count">
                {unreadCount}
              </span>
            )}
          </h1>
        </div>
        {unreadCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={markAllRead}
            data-testid="notifications-mark-all-read"
            title="Markera alla lästa"
          >
            <CheckCheck size={16} />
          </Button>
        )}
      </header>

      <div className="px-4 mt-3">
        <PushActivationCard />

        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setOnlyUnread(false)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition ${
              !onlyUnread ? "bg-[color:var(--jarvis)] text-white" : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
            }`}
            data-testid="notifications-filter-all"
          >
            Alla ({items.length})
          </button>
          <button
            onClick={() => setOnlyUnread(true)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition ${
              onlyUnread ? "bg-[color:var(--jarvis)] text-white" : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
            }`}
            data-testid="notifications-filter-unread"
          >
            Endast olästa
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-[color:var(--text-tertiary)]" />
          </div>
        ) : items.length === 0 ? (
          <div className="card-soft p-8 text-center" data-testid="notifications-empty">
            <BellOff className="w-10 h-10 mx-auto mb-3 text-[color:var(--text-tertiary)]" />
            <p className="text-sm font-medium">
              {onlyUnread ? "Inga olästa meddelanden" : "Inga meddelanden"}
            </p>
            <p className="text-xs text-[color:var(--text-tertiary)] mt-1">
              Här samlas alla pushar du fått.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <NotificationRow
                key={it.id}
                item={it}
                onOpen={() => openDeepLink(it)}
                onMarkRead={() => markRead(it.id)}
                onDelete={() => remove(it.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function NotificationRow({ item, onOpen, onMarkRead, onDelete }) {
  const isRead = !!item.read_at;
  const dt = item.created_at ? new Date(item.created_at) : null;
  const dateStr = dt
    ? dt.toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" })
    : "";
  const hasDeepLink = !!item.data?.url;

  return (
    <li
      className={`card-soft p-3 transition ${isRead ? "opacity-70" : ""}`}
      data-testid={`notification-${item.id}`}
    >
      <div className="flex items-start gap-2.5">
        {!isRead && (
          <span className="w-2 h-2 rounded-full bg-[color:var(--jarvis)] flex-shrink-0 mt-1.5" aria-label="Oläst" />
        )}
        <div className="flex-1 min-w-0">
          <button
            onClick={onOpen}
            disabled={!hasDeepLink}
            className={`text-left w-full ${hasDeepLink ? "cursor-pointer" : "cursor-default"}`}
            data-testid={`notification-open-${item.id}`}
          >
            <p className={`text-sm leading-snug ${isRead ? "font-normal" : "font-semibold"}`}>
              {item.title}
            </p>
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed whitespace-pre-wrap break-words mt-1">
              {item.body}
            </p>
          </button>
          <div className="flex items-center justify-between gap-2 mt-2">
            <p className="text-[11px] text-[color:var(--text-tertiary)] tabular-nums">{dateStr}</p>
            <div className="flex items-center gap-1">
              {hasDeepLink && (
                <button
                  onClick={onOpen}
                  className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--jarvis)]"
                  title="Öppna"
                  data-testid={`notification-deeplink-${item.id}`}
                >
                  <ExternalLink size={13} />
                </button>
              )}
              {!isRead && (
                <button
                  onClick={onMarkRead}
                  className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)]"
                  title="Markera som läst"
                  data-testid={`notification-mark-read-${item.id}`}
                >
                  <Check size={13} />
                </button>
              )}
              <button
                onClick={onDelete}
                className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--danger)]"
                title="Radera"
                data-testid={`notification-delete-${item.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}
